import React, { useEffect } from "react";
import { Steps, Button, Card, Alert, Row, Col } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useBookingWizard } from "../../contexts/BookingWizardContext";
import { TransportDetails } from "./steps/TransportDetails";
import { SupplierDetails } from "./steps/SupplierDetails";
import { ProductDetails } from "./steps/ProductDetails";
import { BasicDetails } from "./steps/BasicDetails";
import { DeliveryDetails } from "./steps/DeliveryDetails";
import { ReviewDetails } from "./steps/ReviewDetails";
import { useTranslation } from "react-i18next";
import { PricingCalculatorHorizontal } from "./PricingCalculatorHorizontal";
import { PricingCalculatorEnhanced } from "./PricingCalculatorEnhanced";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { useLocation, useSearchParams } from "react-router-dom";
import { ProductFormProvider } from "../../contexts/ProductFormContext";
import { getEnv } from "../../lib/env";

interface Step {
  title: string;
  component: React.ComponentType;
}

interface BookingWizardProps {
  onClose: () => void;
}

export const BookingWizard = ({ onClose }: BookingWizardProps) => {
  const { state, updateStep, setCurrentStep } = useBookingWizard();
  const { t } = useTranslation();
  const { isPricingCalculatorEnabled } = useFeatureFlags();
  const location = useLocation();

  // Log the current step on mount and when it changes
  useEffect(() => {
    console.log("BookingWizard - Current step:", state.currentStep);
  }, [state.currentStep]);

  // Force step to returnToStep from location if it exists
  useEffect(() => {
    const returnToStep = location.state?.returnToStep;
    const forceStep = location.state?.forceStep;
    // Use forceStep as priority if available, otherwise fallback to returnToStep
    const targetStep = typeof forceStep === "number" ? forceStep : returnToStep;

    if (typeof targetStep === "number" && targetStep !== state.currentStep) {
      console.log("BookingWizard - Found step in location state:", targetStep);
      console.log("BookingWizard - Current step is:", state.currentStep);
      console.log("BookingWizard - Setting step...");

      // Set timeout to ensure this happens after any other state updates
      const setStepWithTimeout = (delay: number, attempt: number = 1) => {
        setTimeout(() => {
          if (state.currentStep !== targetStep) {
            console.log(
              `BookingWizard - Setting step${attempt > 1 ? " again" : ""} to:`,
              targetStep
            );
            setCurrentStep(targetStep);

            // If we haven't tried too many times yet, schedule another attempt
            if (attempt < 5) {
              setStepWithTimeout(delay * 1.5, attempt + 1);
            }
          }
        }, delay);
      };

      // Start the sequence
      setStepWithTimeout(300);
    }
  }, [location.state, state.currentStep, setCurrentStep]);

  // Define which step to start showing the pricing calculator (0-indexed)
  const PRICING_CALCULATOR_START_STEP = 2; // Step 3 (ProductDetails) is index 2

  // Determine if we should show the pricing calculator for the current step
  const shouldShowPricingCalculator =
    isPricingCalculatorEnabled &&
    state.currentStep >= PRICING_CALCULATOR_START_STEP;

  // Feature flag to use template-based pricing system
  const useTemplatePricingSystem =
    getEnv("VITE_USE_TEMPLATE_PRICING", "true") === "true";

  const steps: Step[] = [
    {
      title: t("bookingWizard.steps.transportDetails"),
      component: TransportDetails,
    },
    {
      title: t("bookingWizard.steps.supplierDetails"),
      component: SupplierDetails,
    },
    {
      title: t("bookingWizard.steps.productDetails"),
      component: ProductDetails,
    },
    {
      title: t("bookingWizard.steps.basicDetails"),
      component: BasicDetails,
    },
    {
      title: t("bookingWizard.steps.deliveryDetails"),
      component: DeliveryDetails,
    },
    {
      title: t("bookingWizard.steps.review"),
      component: ReviewDetails,
    },
  ];

  const CurrentStepComponent = steps[state.currentStep].component;

  const handleStepClick = async (step: number) => {
    if (step < state.currentStep) {
      await updateStep(step, {});
    }
  };

  return (
    <div>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("bookingWizard.title")}</span>
            <Button onClick={onClose} icon={<CloseOutlined />} />
          </div>
        }
        style={{ width: "100%" }}
        bodyStyle={{ padding: "24px" }}
      >
        {state.status === "rejected" && state.rejection_reason && (
          <Alert
            message={t("bookingWizard.rejectionReason")}
            description={state.rejection_reason}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Steps
          current={state.currentStep}
          onChange={handleStepClick}
          items={steps.map((step) => ({ title: step.title }))}
        />

        {shouldShowPricingCalculator ? (
          <ProductFormProvider>
            <div style={{ display: "flex", marginTop: 24, gap: "8px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CurrentStepComponent />
              </div>
              {useTemplatePricingSystem ? (
                <PricingCalculatorEnhanced
                  useTemplatePricingSystem={true}
                />
              ) : (
                <PricingCalculatorHorizontal />
              )}
            </div>
          </ProductFormProvider>
        ) : (
          <CurrentStepComponent />
        )}
      </Card>
    </div>
  );
};
